import * as React from 'react';
import { registerComponent, resolveSyncIfLoadedOrThrow } from '@avensia/excite';
import { styled, type StyledDecorator, type StyledComponent } from '@glitz/react';

type AvailableTags = keyof HTMLElementTagNameMap;

export type StyleDecorators = {
  [tagName in AvailableTags]?: StyledDecorator;
};

type HtmlDecorator<TData> = StyledComponent<
  TData & React.PropsWithChildren<{ attributes: { [item: string]: unknown } }>
>;

type PropType = {
  elements: Array<string | Excite.ContentApi.HtmlElementApiModel>;
  styleDecorators?: StyleDecorators;
  decorators?: Decorators;
  args?: { [placeholder: string]: string | number | React.ReactNode };
};
export default function Document(props: PropType) {
  return (
    <>
      {props.elements?.map((element, index) =>
        renderHtmlTagOrComponent(element, index, props.styleDecorators, props.decorators, props.args),
      )}
    </>
  );
}

registerComponent<Excite.Contentful.DocumentApiModel>(props => <Document {...props} />);

export type Decorators = { [tagName in AvailableTags | '*']?: HtmlDecorator<any> };
const globalDecorators: Decorators = {};
export function registerDecorator<TData>(tagName: AvailableTags | '*', decorator: HtmlDecorator<any>) {
  if (globalDecorators[tagName]) {
    console.warn(`Already register a decorator for html tag ${tagName}`);
  } else {
    globalDecorators[tagName] = decorator;
  }
}

type ItemType = Excite.ContentApi.HtmlElementApiModel | Excite.ContentApi.IComponent | string;

function isComponent(item: ItemType): item is Excite.ContentApi.IComponent {
  return Boolean((item as Excite.ContentApi.IComponent)?.componentName);
}

function isElement(item: ItemType): item is Excite.ContentApi.HtmlElementApiModel {
  return Boolean((item as Excite.ContentApi.HtmlElementApiModel)?.tagName);
}

export function renderHtmlTagOrComponent(
  item: ItemType,
  index: number,
  styleDecorators?: StyleDecorators,
  decorators?: Decorators,
  args?: { [placeholder: string]: string | number | React.ReactNode },
): React.ReactNode {
  const key = index;
  if (isElement(item)) {
    let children = Array.isArray(item.children)
      ? item.children.map((e, i) => renderHtmlTagOrComponent(e, i, styleDecorators, decorators, args))
      : item.children;
    if (typeof children === 'string' && args) {
      children = replaceArgs(children, args);
    }
    const Result =
      decorators?.[item.tagName as AvailableTags] ??
      (globalDecorators[item.tagName as AvailableTags] || globalDecorators['*']);
    const styledDecorator = styleDecorators && styleDecorators[item.tagName as AvailableTags];

    if (Result) {
      return (
        <Result key={key} css={styledDecorator} {...item}>
          {children}
        </Result>
      );
    }

    let styledComponent = (styled as any)[item.tagName[0].toUpperCase() + item.tagName.slice(1)];
    // A tag is replaced with it's styled counterpart
    // eg. "div" is replaced with a "styled.Div".
    if (styledComponent === undefined) {
      styledComponent = styled.Div;
      if (__DEV__) {
        console.error(`Element '${item.tagName}' is not supported, will fallback to div instead.`);
      }
    }
    return React.createElement(styledComponent, { key, css: styledDecorator, ...item.attributes }, children);
  } else if (isComponent(item)) {
    const Component = resolveSyncIfLoadedOrThrow(item.componentName);
    const model = Object.assign({ key }, item);
    return React.createElement(Component, model);
  } else {
    if (typeof item === 'string') {
      if (args) {
        return <React.Fragment key={key}>{replaceArgs(item, args)}</React.Fragment>;
      }
      return item;
    } else {
      if (item) {
        console.warn('Item is not a valid react element', item);
      }
      return null;
    }
  }
}

function replaceArgs(item: string, args: { [placeholder: string]: React.ReactNode }) {
  const children: Array<string | number | React.ReactNode> = [];
  let insidePlaceholder = false;
  let currentPlaceholder = '';
  let currentText = '';

  for (const chr of item) {
    if (chr === '{') {
      insidePlaceholder = true;
      children.push(currentText);
      currentText = '';
    } else if (chr === '}') {
      insidePlaceholder = false;
      children.push(args[currentPlaceholder]);
      currentPlaceholder = '';
    } else if (insidePlaceholder) {
      currentPlaceholder += chr;
    } else {
      currentText += chr;
    }
  }
  if (currentText) {
    children.push(currentText);
  }

  return children.map((text, i) => <React.Fragment key={i}>{text}</React.Fragment>);
}
